// Copyright ©️ 2026 eVolve MEP, LLC

import { type TaskData, formatDate } from '@evolvemep/api-ts';
import type { ColDef, ValueFormatterParams } from 'ag-grid-enterprise';

import { AttachmentsPopover } from 'components/Mantine/Attachments/AttachmentsPopover';
import { CellRenderer } from 'components/Mantine/CellRenderer';
import { hoursColDef } from 'components/Mantine/commonColumnDefs';
import { EntityStatusBadge } from 'components/Mantine/EntityStatusBadge';
import { EvolveLink } from 'components/Mantine/Navigation/EvolveLink';
import { lateColor } from 'constants/badgeMappingStatus';
import { lockedColDef } from 'helpers/ag-grid/baseColumnDef';
import { formatQuantity } from 'helpers/formatQuantity';
import { getDaysRemainingWorkCellTask } from 'modules/Shop/Tasks/common';
import { convertMantineColorToCss } from 'styles/theme/useGenerateMantineTheme';

export const getTaskDataColumnDefs = (): ColDef<TaskData>[] => [
  {
    field: 'facilityName',
  },
  {
    field: 'projectName',
  },
  {
    field: 'workOrderStateName',
    headerName: 'Work Order Status',
    cellRenderer: CellRenderer<TaskData, 'workOrderStateName'>(({ value }) => (
      <EntityStatusBadge customStatusEntityName="Work Order" stateName={value} />
    )),
    width: 150,
  },
  {
    field: 'workOrderName',
    headerName: 'Work Order Id',
    cellRenderer: CellRenderer<TaskData>(({ value, data }) => (
      <EvolveLink to={`/work-orders/${data.workOrderId}`} from="Task Data">
        {value}
      </EvolveLink>
    )),
  },
  {
    field: 'workOrderName',
    headerName: 'Work Order Name',
  },
  {
    field: 'workOrderItemName',
    headerName: 'Item',
  },
  {
    field: 'quantity',
    headerName: 'Item Qty',
    valueFormatter: ({ value }: { value: number | undefined }) => formatQuantity(value),
    width: 100,
  },
  {
    colId: 'unitTime',
    headerName: 'Actual Unit Time',
    headerTooltip: 'Task Time ÷ Item Qty',
    valueGetter: ({ data }) => (data?.totalTimeInSeconds ? data.totalTimeInSeconds / data.quantity : 0),
    ...hoursColDef(),
    sortable: false,
    width: 120,
  },
  {
    field: 'taskName',
    headerName: 'Task Name',
    lockVisible: true,
    cellRenderer: CellRenderer<TaskData>(({ data, value }) => (
      <EvolveLink to={`/task-viewer/${data.taskId}`} from="Task Data">
        {value}
      </EvolveLink>
    )),
  },
  {
    field: 'taskStatusTypeName',
    headerName: 'Task Status',
    cellRenderer: CellRenderer<TaskData, 'taskStatusTypeName'>(({ value }) => (
      <EntityStatusBadge customStatusEntityName="Task" stateName={value} />
    )),
    width: 150,
  },
  {
    field: 'comments',
    headerName: 'Task Comments',
    tooltipField: 'comments',
    sortable: false,
  },
  {
    field: 'workCellName',
    headerName: 'Work Cell',
  },
  {
    field: 'adjustedTimeInSeconds',
    headerName: 'Adjusted Time',
    ...hoursColDef(),
    width: 125,
  },
  {
    field: 'totalTimeEntriesInSeconds',
    headerName: 'Logged Time',
    ...hoursColDef(),
    width: 125,
  },
  {
    field: 'estimatedTimeInSeconds',
    headerName: 'Estimated Time',
    headerTooltip: 'The Estimated Unit time of this task multiplied by the item quantity.',
    ...hoursColDef(),
    width: 160,
  },
  {
    field: 'runTimeInSeconds',
    headerName: 'Run Time',
    ...hoursColDef(),
    width: 125,
  },
  {
    field: 'totalTimeInSeconds',
    headerName: 'Task Time',
    headerTooltip: 'Uses Adjusted time first, then Logged time, then Run time.',
    ...hoursColDef(),
    width: 150,
  },
  {
    field: 'needByDate',
    headerName: 'Need By Date',
    width: 125,
    valueFormatter: ({ value }: ValueFormatterParams<TaskData, TaskData['needByDate']>) =>
      formatDate(value, 'MM/dd/yyyy'),
    cellStyle: ({ data }) =>
      data && getDaysRemainingWorkCellTask(data) < 0 ? { color: convertMantineColorToCss(lateColor) } : undefined,
  },
  {
    field: 'onSiteDate',
    headerName: 'On-site Date',
    width: 125,
    valueFormatter: ({ value }: ValueFormatterParams<TaskData, TaskData['onSiteDate']>) =>
      formatDate(value, 'MM/dd/yyyy'),
  },
  {
    field: 'dateStarted',
    width: 150,
    valueFormatter: ({ value }: ValueFormatterParams<TaskData, TaskData['dateStarted']>) =>
      formatDate(value, 'MM/dd/yyyy'),
  },
  {
    field: 'dateCompleted',
    width: 150,
    valueFormatter: ({ value }: ValueFormatterParams<TaskData, TaskData['dateCompleted']>) =>
      formatDate(value, 'MM/dd/yyyy'),
  },
  {
    ...lockedColDef('right', false),
    width: 80,
    cellRenderer: CellRenderer<TaskData>(
      ({ data }) => <AttachmentsPopover taskId={data.taskId} numberOfAttachments={data.numberOfAttachments} />,
      {
        flexProps: { justify: 'flex-end' },
      },
    ),
  },
];
